"use client";

import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Avatar,
  Tag,
  Button,
  Input,
  Dropdown,
  Space,
  Typography,
  Row,
  Col,
  Segmented,
  message,
  DatePicker,
  Spin,
  Empty,
  Tooltip,
  Skeleton,
  Menu,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  CalendarOutlined,
  TagOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../../api";
import RejectionModal from "./RejectionModal";
import { useNavigate } from "react-router-dom";
import { getDateTimeString } from "../../Commonfunctions";
import "./Directory.css"

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function Directory() {
  const [search, setSearch] = useState("");
  const [toggleUser, setToggleUser] = useState("vendor");
  const [registrationData, setRegistrationData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [staffFilteredData, setStaffFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorDateRange, setVendorDateRange] = useState(null);
  const [staffDateRange, setStaffDateRange] = useState(null);
  const [selectedMembershipType, setSelectedMembershipType] = useState(null);
  const [vendorStatusFilter, setVendorStatusFilter] = useState("all");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all")
  const [staffStatusFilter, setStaffStatusFilter] = useState("all");
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [filterType, setFilterType] = useState("All");
  const [onClickData, setOnclickData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const statusColors = {
    approved: "green",
    pending: "orange",
    rejected: "red",
  };

  const pageMenu = (
    <Menu
      onClick={(e) => {
        setItemsPerPage(Number(e.key));
        setCurrentPage(1); // reset to first page
      }}
      items={[5, 10, 25, 50].map((n) => ({ key: n.toString(), label: n }))}
    />
  );


  const success = () => {
    messageApi.success("Registration Successfully Approved");
  };

  const error = () => {
    messageApi.error("Registration Successfully Rejected");
  };

  const getRegistrationData = async () => {
    setLoading(true);
    setRegistrationData([]);
    setFilteredData([]);
    setStaffData([]);
    setStaffFilteredData([]);
    try {
      const requestData = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };
      const response = await api.userRegistration(requestData);
      if (response.data.status === "1") {
        const allData = [...response.data.printer_data, ...response.data.vendor_data];
        const uniqueAllData = Array.from(
          new Map(allData.map(item => [item.fld_slno, item])).values()
        ).sort((a, b) => {
          const dateA = new Date(
            a.fld_modified_datetime || a.fld_system_inserted_datetime
          );

          const dateB = new Date(
            b.fld_modified_datetime || b.fld_system_inserted_datetime
          );

          return dateB - dateA;
        });

        const uniqueStaffData = Array.from(
          new Map(
            response.data.staff_registration_data.map(item => [item.fld_slno, item])
          ).values()
        ).sort((a, b) => {
          const dateA = new Date(
            a.fld_modified_datetime || a.fld_system_inserted_datetime
          );

          const dateB = new Date(
            b.fld_modified_datetime || b.fld_system_inserted_datetime
          );

          return dateB - dateA;
        });

        setRegistrationData(uniqueAllData);
        setFilteredData(uniqueAllData);
        setStaffData(uniqueStaffData);
        setStaffFilteredData(uniqueStaffData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRegistrationData();
  }, []);


  // Apply filters
  useEffect(() => {
    let data = toggleUser === "vendor" ? registrationData : staffData;
    let filtered = [...data];

    // Date filter
    const dateRange = toggleUser === "vendor" ? vendorDateRange : staffDateRange;
    if (dateRange?.[0] && dateRange?.[1]) {
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.fld_system_inserted_datetime);
        return itemDate.isAfter(dateRange[0].startOf("day")) && itemDate.isBefore(dateRange[1].endOf("day"));
      });
    }

    // Printer/Vendor filter (from fld_role_name)
    if (toggleUser === "vendor" && selectedRoleFilter !== "all") {
      filtered = filtered.filter(
        (item) =>
          item.fld_role_name &&
          item.fld_role_name.toLowerCase() === selectedRoleFilter.toLowerCase()
      );
    }

    // Membership type filter
    if (selectedMembershipType && toggleUser === "vendor") {
      filtered = filtered.filter((item) => item.fld_membership_type_name === selectedMembershipType);
    }

    // Status filter
    const statusFilter = toggleUser === "vendor" ? vendorStatusFilter : staffStatusFilter;
    if (statusFilter === "approved") {
      filtered = filtered.filter((item) => item.fld_is_approval_rejection === "1");
    } else if (statusFilter === "pending") {
      filtered = filtered.filter((item) => !item.fld_is_approval_rejection || item.fld_is_approval_rejection === "0");
    } else if (statusFilter === "rejected") {
      filtered = filtered.filter((item) => item.fld_is_approval_rejection === "2");
    }

    if (toggleUser === "vendor") setFilteredData(filtered);
    else setStaffFilteredData(filtered);
  }, [
    vendorDateRange,
    staffDateRange,
    selectedMembershipType,
    vendorStatusFilter,
    staffStatusFilter,
    selectedRoleFilter,
    registrationData,
    staffData,
  ]);






  const getCurrentDateRange = () => {
    return toggleUser === "vendor" ? vendorDateRange : staffDateRange;
  };

  // Get current status filter based on toggle
  const getCurrentStatusFilter = () => {
    return toggleUser === "vendor" ? vendorStatusFilter : staffStatusFilter;
  };

  // Format date for display: 5-Mar-2025
  const formatDateDisplay = (date) => {
    if (!date) return '';
    return date.format('D-MMM-YYYY');
  };

  const paginatedData = (toggleUser === "vendor" ? filteredData : staffFilteredData).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(
    (toggleUser === "vendor" ? filteredData.length : staffFilteredData.length) / itemsPerPage
  );




  const StatusBadge = ({ status }) => {
    switch (status) {
      case "approved":
      case "1":
        return (
          <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontWeight: 500 }}>
            Approved
          </Tag>
        );
      case "pending":
      case "0":
      case "":
        return (
          <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontWeight: 500 }}>
            Pending
          </Tag>
        );
      case "rejected":
      case "2":
        return (
          <Tag color="red" icon={<CloseCircleOutlined />} style={{ fontWeight: 500 }}>
            Rejected
          </Tag>
        );
      default:
        return <Tag>-</Tag>;
    }
  };
  const handleFilterTypeChange = (value) => {
    setFilterType(value);

    // Update the relevant filter based on user type (vendor/staff)
    const normalized = value.toLowerCase(); // "all", "pending", etc.

    if (toggleUser === "vendor") {
      if (normalized === "all") setVendorStatusFilter("all");
      else if (normalized === "pending") setVendorStatusFilter("pending");
      else if (normalized === "approved") setVendorStatusFilter("approved");
      else if (normalized === "rejected") setVendorStatusFilter("rejected");
    } else {
      if (normalized === "all") setStaffStatusFilter("all");
      else if (normalized === "pending") setStaffStatusFilter("pending");
      else if (normalized === "approved") setStaffStatusFilter("approved");
      else if (normalized === "rejected") setStaffStatusFilter("rejected");
    }
  };

  const handleClick = (type, data) => {
    setOnclickData(data);
    setPendingAction(type); // store what action is pending
    setOpenRejectionModal(true); // open the modal for both approve and reject
  };

  const handleCardClick = (item) => {
    navigate(`member/${item.fld_company_press_id}`, {
      state: { memberData: item },
    })
  }


  const handleApproveReject = (type, rejectReason) => {
    if (!onClickData) return;
    const payload = {
      master_tbl_register: [
        {
          ...onClickData,
          fld_bussiness_type: onClickData.fld_bussiness_type || "",
          fld_bussiness_type_file_upload_path: onClickData.fld_bussiness_type_file_upload_path || "",
          fld_no_of_employee: onClickData.fld_no_of_employee || "",
          fld_additional_contacts: onClickData.fld_additional_contacts || "",
          fld_additional_emails: onClickData.fld_additional_emails || "",

          fld_is_approval_rejection: type === "approve" ? "1" : "2",
          fld_is_approval_rejection_name: type === "approve" ? "approved" : "rejected",
          fld_reason_for_rejection: rejectReason || "",
          fld_approval_rejection_date: getDateTimeString(),
        },
      ],
      synceddatetime: getDateTimeString(),
      formcode: "101",
      appversion: "1.0.0.0",
      apikey: "kavin",
      apptypeno: "3",
    };
    api.staffRegistrationApproveReject(payload).then((res) => {
      if (res.data.status === "1") {
        type === "approve" ? success() : error();
        getRegistrationData();
        setOpenRejectionModal(false);
      }
    });
  };

  const handleResetFilters = () => {
    setVendorDateRange(null);
    setStaffDateRange(null);
    setSelectedMembershipType(null);
    setVendorStatusFilter("all");
    setSelectedRoleFilter("all");
    setStaffStatusFilter("all");
    messageApi.success("Filters reset successfully");
  };

  const SkeletonCard = () => (
    <Card
      style={{
        borderRadius: 10,
        marginBottom: 10,
      }}
      bodyStyle={{
        padding: "10px 14px",
      }}
    >
      <Row align="middle">
        <Col flex="auto">
          <Space size="middle" align="center">
            <Skeleton.Avatar active size={40} />

            <div>
              <Skeleton.Input
                active
                size="small"
                style={{
                  width: 180,
                  height: 16,
                  marginBottom: 4,
                }}
              />
              <br />
              <Skeleton.Input
                active
                size="small"
                style={{
                  width: 120,
                  height: 14,
                }}
              />
            </div>

            <Skeleton.Input
              active
              size="small"
              style={{
                width: 250,
                height: 16,
                marginLeft: 20,
              }}
            />

            <Skeleton.Input
              active
              size="small"
              style={{
                width: 180,
                height: 16,
                marginLeft: 20,
              }}
            />
          </Space>
        </Col>
      </Row>
    </Card>
  );
  return (
    <>
      {contextHolder}
      <Layout style={{ minHeight: "100vh", background: "#fafafa" }} >
        <Header style={{ background: "#fff", borderBottom: "1px solid #f0f0f0" }} className="ant-layout-header">
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                placeholder="Search for Company Name..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 400, background: '#efeff4ff' }}
              />
            </Col>
            {/* <Col>
              <Button icon={<DownloadOutlined />}>Export</Button>
            </Col> */}
          </Row>
        </Header>

        <Content className="approval-content">
          <div style={{ maxWidth: 1250, margin: "0 auto" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <h2 level={2} style={{ margin: 0 }} className="approve-title">
                  Approval Dashboard
                </h2>
                <Text type="secondary">Review and manage pending approvals</Text>
              </Col>
              <Col>
                <Segmented
                  options={[
                    { label: "Life Member/Service Member", value: "vendor" },
                    { label: "Staff", value: "staff" },
                  ]}
                  value={toggleUser}
                  onChange={(val) => { setToggleUser(val); setCurrentPage(1); }}
                  className="custom-segmented"
                />
              </Col>
            </Row>

            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <Space wrap>
                  {/* <Button icon={<FilterOutlined />} /> */}

                  {/* Date Range Filter */}
                  <Dropdown
                    trigger={["click"]}
                    dropdownRender={() => (
                      <div style={{ padding: 12, background: "#fff" }}>
                        <RangePicker
                          value={toggleUser === "vendor" ? vendorDateRange : staffDateRange}
                          format="DD-MMM-YYYY"
                          onChange={(dates) =>
                            toggleUser === "vendor" ? setVendorDateRange(dates) : setStaffDateRange(dates)
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                    )}
                  >
                    <Button icon={<CalendarOutlined />}>
                      {getCurrentDateRange()
                        ? `${formatDateDisplay(getCurrentDateRange()[0])} - ${formatDateDisplay(getCurrentDateRange()[1])}`
                        : "Date"}{" "}
                      <DownOutlined />
                    </Button>
                  </Dropdown>

                  {/* 🧩 Printer / Vendor Role Filter (based on fld_role_name) */}
                  {toggleUser === "vendor" && (
                    <Dropdown
                      menu={{
                        items: [
                          { key: "all", label: "All", onClick: () => setSelectedRoleFilter("all") },
                          { key: "printer", label: "Life Member", onClick: () => setSelectedRoleFilter("printer") },
                          { key: "vendor", label: "Service Member", onClick: () => setSelectedRoleFilter("vendor") },
                        ],
                      }}
                      trigger={["click"]}
                    >
                      <Button>
                        {selectedRoleFilter === "all"
                          ? "All"
                          : selectedRoleFilter.charAt(0).toUpperCase() + selectedRoleFilter.slice(1)}{" "}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}


                  {/* Membership Type Filter (only for vendor) */}
                  {toggleUser === "vendor" && (
                    <Dropdown
                      menu={{
                        items: ["Life", "Annual"].map((type) => ({
                          key: type,
                          label: type,
                          onClick: () =>
                            setSelectedMembershipType(
                              selectedMembershipType === type ? null : type
                            ),
                        })),
                      }}
                      trigger={["click"]}
                    >
                      <Button>
                        {selectedMembershipType || "Type"} <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}

                  {/* Reset Filters */}
                  <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
                    Reset Filter
                  </Button>
                </Space>
              </Col>
              <Col>
                <Segmented
                  className="review-toggle"
                  options={["All", "Pending", "Approved", "Rejected"]}
                  value={filterType}
                  onChange={handleFilterTypeChange}
                />
              </Col>

            </Row>


            <Space direction="vertical" style={{ width: "100%" }} className="directory-card">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              ) :
                paginatedData.length === 0 ? (
                  <div >
                    <Card
                      className="nodata-card"
                    >
                      <Empty
                        description="No data available "
                        image={<InboxOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
                      />
                    </Card>
                  </div>
                ) : (
                  paginatedData.map((item) => (
                    <Card key={`${item.fld_role_name}-${item.fld_slno}`} className="directory-card-content" onClick={() => toggleUser === "vendor" ? handleCardClick(item) : ""} style={{ cursor: toggleUser === "vendor" ? 'pointer' : "" }} >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Avatar
                            size={48}
                            src={item.fld_logo_brand_upload_path || item.avatar}
                            style={{
                              backgroundColor: !item.avatar ? "#eee" : undefined,
                              borderRadius: 8,
                            }}
                          >
                            {item.initial || item.fld_staff_name?.charAt(0)}
                          </Avatar>
                        </Col>
                        <Col flex="auto">
                          <Text strong style={{ display: "block", marginBottom: 4 }}>
                            {item.fld_company_press_name || item.fld_staff_name}{" "}
                            {item.fld_role_name !== 'admin' && <Tag bordered={false} color={item.fld_role_name === 'printer' ? "processing" : "volcano"}>{item.fld_role_name === 'printer' ? 'Life Member' : item.fld_role_name === 'staff' ? 'Staff' : 'Service Member'}</Tag>}
                          </Text>

                          {toggleUser === "vendor" && (
                            <Space size={8}>
                              <Tag>{item.fld_membership_type_name || item.type}</Tag>
                              <Text type="secondary">
                                {item.fld_head_office_address
                                  ? item.fld_head_office_address.length > 70
                                    ? item.fld_head_office_address.slice(0, 70) + "..."
                                    : item.fld_head_office_address
                                  : item.description
                                    ? item.description.length > 70
                                      ? item.description.slice(0, 70) + "..."
                                      : item.description
                                    : ""}
                              </Text>

                            </Space>
                          )}
                          {toggleUser !== "vendor" && (
                            <div style={{ marginTop: 6 }}>
                              {/* First Line — Gender, Age, Mobile */}
                              <Row gutter={[12, 8]} align="middle">
                                <Col>
                                  <Text type="secondary">
                                    🚻 <strong>Gender:</strong> {item.fld_gender_name || "N/A"}
                                  </Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    🎂 <strong>Age:</strong> {item.fld_age || "N/A"}
                                  </Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    📞 <strong>Mobile:</strong> {item.fld_registred_mobile_number || "N/A"}
                                  </Text>
                                </Col>
                              </Row>

                              {/* Second Line — Address, Designation */}
                              <Row gutter={[12, 4]} style={{ marginTop: 4 }}>
                                <Col >
                                  <Text type="secondary">
                                    💼 <strong>Designation:</strong> {item.fld_designation_job_title || "N/A"}
                                  </Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    📍 <strong>Address:</strong>{" "}
                                    {item.fld_address
                                      ? item.fld_address.length > 40
                                        ? item.fld_address.slice(0, 40) + "..."
                                        : item.fld_address
                                      : "N/A"}
                                  </Text>
                                </Col>
                              </Row>
                            </div>
                          )}

                        </Col>

                        <Col>
                          {toggleUser === "vendor" ? (""
                            // <Tooltip title="View complete Details">
                            //   <Button icon={<EyeOutlined />} onClick={(e) => {
                            //     e.stopPropagation()
                            //     navigate(`member/${item.fld_company_press_id}`, {
                            //       state: { memberData: item },
                            //     });
                            //   }} >
                            //     View
                            //   </Button>
                            // </Tooltip>
                          ) : (
                            <Space size="middle" className="approve-buttons">
                              {(item.fld_is_approval_rejection === "0" || !item.fld_is_approval_rejection) && (
                                <>
                                  <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    style={{
                                      background: "linear-gradient(135deg, #73E2A7, #52C41A)",
                                      borderRadius: 8,
                                      fontWeight: 500,
                                      padding: "0 16px",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleClick("approve", item)
                                    }}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    type="primary"
                                    icon={<CloseCircleOutlined />}
                                    style={{
                                      borderRadius: 8,
                                      fontWeight: 500,
                                      background: "linear-gradient(135deg, #FF7A7A, #FF4D4F)",
                                      padding: "0 16px",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClick("reject", item)
                                    }
                                    }
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              {item.fld_is_approval_rejection === "2" && (
                                <Button
                                  type="primary"
                                  icon={<CheckCircleOutlined />}
                                  style={{
                                    background: "#52c41a",
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    padding: "0 16px",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClick("approve", item)
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                              {/* {item.fld_is_approval_rejection === "1" && (
                              <Text type="secondary" style={{ fontStyle: "italic" }}>
                                Approved
                              </Text>
                            )} */}
                            </Space>

                          )}
                        </Col>
                        <Col>
                          {Number(item.fld_modified_no) > 1 &&
                            item.fld_is_approval_rejection === "0" ? (
                            <Tag
                              color="gold"
                              icon={<ClockCircleOutlined />}
                              style={{
                                marginBottom: 6,
                                fontWeight: 500,
                              }}
                            >
                              Profile Edit Request Pending Approval
                            </Tag>
                          ) :
                            <StatusBadge status={item.fld_is_approval_rejection || item.status} />
                          }



                          <div>
                            <Text type="secondary">
                              {item.fld_system_inserted_datetime
                                ? dayjs(item.fld_system_inserted_datetime).format("D-MMM-YYYY")
                                : item.date}
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ))
                )}
            </Space>

            <div className="review-pagination">
              <Button
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              />
              <span className="page-info">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                icon={<RightOutlined />}
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              />
              <Dropdown
                overlay={pageMenu}
                trigger={["click"]}
              >
                <Button>
                  {itemsPerPage} / page <DownOutlined />
                </Button>
              </Dropdown>
            </div>

            {openRejectionModal && (
              <RejectionModal
                title={
                  pendingAction === "approve"
                    ? "Approve Application"
                    : "Reject Application"
                }
                content={
                  pendingAction === "approve"
                    ? "Are you sure you want to approve this application?"
                    : "Are you sure you want to reject this application?"
                }
                buttonName={pendingAction === "approve" ? "Approve" : "Reject"}
                open={openRejectionModal}
                onClose={() => setOpenRejectionModal(false)}
                onSubmit={(reason) => handleApproveReject(pendingAction, reason)}
              />
            )}

          </div>
        </Content>
      </Layout>
    </>
  );
}
